import { useState, useRef, useContext } from "react";
import { Alert, Breadcrumbs, Button, Card, CardContent, CircularProgress, IconButton } from "@mui/material";
import { Link, Paper, Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import MapSecteurAgence from "../components/map/mapSecteurAgence";
import CloseIcon from '@mui/icons-material/Close';
import _ from 'lodash';
import { buildSecteurAffecteEtab } from "../service/regions_foncia";
import { AppConfigContext } from "../context";
import BackendApiService from "../service/BackendApiService";
import { useOktaAuth } from "@okta/okta-react";

function SecteurAgence() {

    const appConfig = useContext(AppConfigContext);
    const { authState } = useOktaAuth();

    const theme = useTheme();

    const [selectedEtablissement, setSelectedEtablissement] = useState(null);
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [metier, setMetier] = useState('gl');
    const [etablissements, setEtablissements] = useState([]);

    const [loading, setLoading] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [feedback, setFeedback] = useState(null);

    const [affectationMode, setAffectationMode] = useState(false);

    const mapRef = useRef();

    const service = new BackendApiService(appConfig.app.backendApiUrl, authState);

    const showEtablissementsByRegion = function (nomRegion) {
        let errorStatus;
        service.fetchEtablissements(nomRegion)
            .then(res => {
                if (!res.ok) {
                    errorStatus = res.status;
                    return res.text();
                }
                return res.json();
            })
            .then(data => {
                if (errorStatus)
                    throw new Error(errorStatus + ' ' + data);

                setEtablissements(data);
                mapRef.current.showEtablissements(data);
            })
            .catch(error => {
                handleOpenSnackbar({ severity: 'error', msg: 'Erreur survenue: ' + error.message });
            })
            .finally(() => setLoading(false));
    }

    const handleSauvegarder = function (e) {

        setLoading(true);

        let errorStatus;
        service.saveAffectationsEtablissement(selectedEtablissement, metier)
            .then(res => {
                if (!res.ok) {
                    errorStatus = res.status;
                    return res.text();
                }

                showEtablissementsByRegion(selectedRegion.nom);
                handleOpenSnackbar({ severity: 'success', msg: 'Sauvegarde OK' });
                disableAffectationMode();
                return;
            })
            .then(data => {
                if (errorStatus)
                    throw new Error(errorStatus + ' ' + data);
            })
            .catch(error => {
                setFeedback({ severity: 'error', msg: 'Erreur survenue: ' + error.message });
                setOpenSnackbar(true);
            })
            .finally(() => setLoading(false));

    }

    const disableAffectationMode = function (e) {
        setAffectationMode(false);
        mapRef.current.disableAffectationMode();
    }

    const defineSecteurs = function (metier) {

        setAffectationMode(true);
        setMetier(metier);

        let allSecteurEtab = buildSecteurAffecteEtab(etablissements, metier);

        mapRef.current.enableAffectationMode(allSecteurEtab, metier);

    };


    const handleSelectRegion = function (region) {
        setLoading(true);
        setSelectedRegion(region);

        showEtablissementsByRegion(region.nom);
    }

    const handleSelectCommune = function (commune) {

        let secteurs = selectedEtablissement[metier];
        if (Array.isArray(secteurs)) {
            let found = _.find(secteurs, { code: commune.code });
            if (!found) {
                let newEtab = { ...selectedEtablissement };
                newEtab[metier] = [commune, ...secteurs];
                setSelectedEtablissement(newEtab);
            }
        } else {
            let newEtab = { ...selectedEtablissement };
            newEtab[metier] = [commune];
            setSelectedEtablissement(newEtab);
        }


    }


    const handleRemoveCommune = function (codeCommune) {
        let secteurs = selectedEtablissement[metier];
        let goodSecteurs = _.remove(secteurs, function (c) { return c.code !== codeCommune; });

        let newEtab = { ...selectedEtablissement };
        newEtab[metier] = goodSecteurs;
        setSelectedEtablissement(newEtab);
    }

    const handleCloseSnackbar = function () {
        setOpenSnackbar(false);
    }

    const handleOpenSnackbar = function (feedback) {
        setFeedback(feedback);
        setOpenSnackbar(true);
    }

    const showRegion = function () {
        mapRef.current.showRegion();
    }

    const showFrance = function () {

        setSelectedRegion(null);
        setSelectedEtablissement(null);
        setAffectationMode(false);

        mapRef.current.showFrance();

    }

    return (
        <div className="map-wrap">

            {selectedRegion?.nom &&
                <Card sx={{ minWidth: 250, maxWidth: 400, position: 'absolute', zIndex: 1000, top: '10px', left: '10px' }}>

                    <CardContent>
                        {loading && <Typography sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>Chargement... <CircularProgress size={20} /></Typography>}

                        {!loading &&
                            <>
                                <Breadcrumbs aria-label="breadcrumb">
                                    <Link underline="hover" color="inherit" href="#" onClick={showFrance}>France</Link>
                                    <Link underline="hover" color="text.primary" onClick={showRegion} href="#">{selectedRegion.nom}</Link>
                                </Breadcrumbs>
                                <Typography sx={{ mb: 1.5 }} color="text.secondary">{!loading ? etablissements.length + ' agences' : ''}</Typography>
                            </>
                        }


                        <Typography variant="h5" component="div">
                            {selectedEtablissement?.nomUsage}
                        </Typography>
                        <Typography sx={{ mb: 1.5 }} color="text.secondary">
                            {selectedEtablissement?.denominationSociale}
                        </Typography>
                        <Typography sx={{ mb: 2 }} variant="body2">
                            {selectedEtablissement?.adresseLigne1}
                            <br />
                            {selectedEtablissement?.codePostal} {selectedEtablissement?.ville}
                        </Typography>

                        {affectationMode &&
                            <Paper sx={{ mb: 2, width: '100%', overflow: 'hidden' }}>
                                <TableContainer sx={{ maxHeight: '300px' }}>
                                    <Table size="small" stickyHeader>
                                        {_.isEmpty(selectedEtablissement[metier]) && <caption>Aucun secteur affecté</caption>}
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ backgroundColor: theme.palette[metier].main }}>Secteurs {metier.toUpperCase()}</TableCell>
                                                <TableCell sx={{ backgroundColor: theme.palette[metier].main }} align="right">Code postal</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {selectedEtablissement[metier].map(c =>
                                                <TableRow key={c.code} sx={{ '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover } }}>
                                                    <TableCell component="th" scope="row">{c.nom}</TableCell>
                                                    <TableCell align="right">{c.codesPostaux.join(',')}</TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>

                                </TableContainer>
                            </Paper>
                        }


                        {!loading && selectedEtablissement && !affectationMode &&
                            <Stack direction="row" justifyContent="space-between" spacing={2}>
                                <Button size="small" variant="contained" color="gl" onClick={e => defineSecteurs('gl')}>Secteurs GL</Button>
                                <Button size="small" variant="contained" color="copro" onClick={e => defineSecteurs('copro')}>Secteurs COPRO</Button>
                            </Stack>
                        }

                        {!loading && selectedEtablissement && affectationMode &&
                            <Stack direction="row" justifyContent="space-between" spacing={2}>
                                {selectedEtablissement.secteurAffectable && <Button size="small" variant="contained" color={metier} onClick={handleSauvegarder}> Sauvegarder</Button>}
                                <Button size="small" variant="contained" color={metier} onClick={disableAffectationMode}>
                                    {selectedEtablissement.secteurAffectable ? "Annuler" : "Retour"}
                                </Button>
                            </Stack>
                        }

                    </CardContent>



                </Card>
            }

            <MapSecteurAgence
                ref={mapRef}
                osmUrls={appConfig.app.osmUrls}
                selectedRegion={selectedRegion}
                selectedEtablissement={selectedEtablissement}
                handleSelectRegion={handleSelectRegion}
                handleSelectEtablissement={setSelectedEtablissement}
                handleSelectCommune={handleSelectCommune}
                handleRemoveCommune={handleRemoveCommune}
                handleOpenSnackbar={handleOpenSnackbar}
                theme={theme}>
            </MapSecteurAgence>

            <Snackbar open={openSnackbar}
                autoHideDuration={6000} onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                action={<IconButton size="small" aria-label="close" onClick={handleCloseSnackbar}><CloseIcon fontSize="small" /></IconButton>}
            >
                {feedback && <Alert severity={feedback.severity} onClose={handleCloseSnackbar}>{feedback.msg}</Alert>}
            </Snackbar>

        </div>
    );
}

export default SecteurAgence;
